import { useEffect, useState } from 'react';
import { IconX } from '@tabler/icons-react';
import { Dialog, DialogClose, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { youtubeEmbedUrl } from '@/utils/youtube';
import { useTranslation } from '../../i18n/hooks/useTranslation';

// A cross-origin-isolated page (COOP/COEP — the Vite dev server sends them for ffmpeg + background removal, see vite.config.js)
// refuses to render a cross-origin iframe unless it is `credentialless`, and the attribute has to be there when the iframe is
// created. A page that is not isolated gets a plain iframe (same rule as the payment popup in BuyCreditTab).
const FRAME_ISOLATION = typeof window !== 'undefined' && window.crossOriginIsolated && 'credentialless' in HTMLIFrameElement.prototype
    ? { credentialless: '' }
    : {};

// The window is the video and nothing else, as big as the screen allows: the widest 16:9 that fits with 12px of air all round —
// the whole width of a narrow screen, a big window on a large one. Written out in full so Tailwind sees the classes.
const WINDOW_SIZE = 'aspect-video w-[min(calc(100vw-24px),calc((100vh-24px)*16/9))]';

// The close button. The player is a cross-origin iframe: the page is never told where the pointer is while it is over the video,
// so "show the × when the window is hovered" can't work (`:hover` is false over the iframe). What the page does see is its own
// pixels — a 36px square in the top-right corner (CORNER) sits above the player (clear of the player's own buttons, which start
// ~38px from the edge) and shows the × while the pointer is in it. On a mouse the × also shows for a moment when the window opens,
// so people find it; on a touch screen there is no hover, so it stays visible. Keyboard focus shows it too.
const CORNER = 'group/corner absolute right-0 top-0 z-10 h-9 w-9';
const CLOSE_BUTTON = 'absolute inset-1 inline-flex items-center justify-center rounded-full bg-black/60 text-white backdrop-blur-sm transition-opacity hover:bg-black/80 focus:outline-none focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-white/80 [@media(hover:hover)]:group-hover/corner:opacity-100';
const CLOSE_HIDDEN = '[@media(hover:hover)]:opacity-0';
const CUE_MS = 2500;

/**
 * A YouTube video in a window. `videoId` must come from youtubeVideoId (utils/youtube.js) — the player address is built from it
 * alone. The video starts as soon as the window opens and stops when it closes (the player goes away with the window).
 * `title` is only for screen readers: the window shows the video alone. For a ready-made trigger see <VideoHelpButton>.
 */
export default function VideoModal({ open, onClose, videoId, title }) {
    const { t } = useTranslation();
    const [cue, setCue] = useState(true);

    useEffect(() => {
        if (!open) return undefined;
        setCue(true);
        const id = setTimeout(() => setCue(false), CUE_MS);
        return () => clearTimeout(id);
    }, [open]);

    return (
        <Dialog open={open} onOpenChange={(next) => { if (!next) onClose?.(); }}>
            <DialogContent
                aria-describedby={undefined}
                showClose={false}
                // above the floating help / WhatsApp buttons (z-100 / z-190), like the editor windows
                className={`z-[200] max-w-none gap-0 overflow-hidden rounded-2xl bg-black p-0 dark:bg-black ${WINDOW_SIZE}`}
                overlayClassName="z-[199]"
            >
                <DialogTitle className="sr-only">{title || 'Video'}</DialogTitle>
                {/* First in the order so the window opens with the focus here, not inside the player — the player keeps every key
                    (Esc included) once it has the focus. */}
                <div className={CORNER}>
                    <DialogClose aria-label={t('common.close')} className={cn(CLOSE_BUTTON, !cue && CLOSE_HIDDEN)}>
                        <IconX size={16} />
                    </DialogClose>
                </div>
                <iframe
                    {...FRAME_ISOLATION}
                    src={youtubeEmbedUrl(videoId)}
                    title={title || 'Video'}
                    className="h-full w-full border-0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin"
                />
            </DialogContent>
        </Dialog>
    );
}
